import { useId, useState } from 'react';
import type { PasswordFieldProps } from '@/features/auth/types/PasswordFieldProps';
import { adminAuth } from '@/i18n/dictionaries/adminAuth';
import { useTranslation } from '@/i18n/useTranslation';

/**
 * A password box with "Arată parola": a toggle beside it, pressed while the text shows, so a long
 * password can be checked before it is sent. The error and the hint are its description.
 */
export function PasswordField({
  id,
  label,
  value,
  onChange,
  autoComplete,
  error,
  hint,
  inputRef,
}: PasswordFieldProps) {
  const { t } = useTranslation(adminAuth);
  const [shown, setShown] = useState(false);
  const hintId = useId();
  const errorId = useId();
  const described = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');
  return (
    <div className="admin-auth__field">
      <label className="admin-auth__label" htmlFor={id}>
        {label}
      </label>
      <div className="admin-auth__password">
        <input
          ref={inputRef}
          id={id}
          className="text-input"
          type={shown ? 'text' : 'password'}
          autoComplete={autoComplete}
          spellCheck={false}
          value={value}
          aria-invalid={error ? true : undefined}
          aria-describedby={described || undefined}
          onChange={(e) => onChange(e.target.value)}
        />
        <button
          type="button"
          className="btn btn--outline btn--sm"
          aria-pressed={shown}
          aria-controls={id}
          onClick={() => setShown(!shown)}
        >
          {shown ? t('hidePassword') : t('showPassword')}
        </button>
      </div>
      {hint && (
        <p className="admin-auth__hint" id={hintId}>
          {hint}
        </p>
      )}
      {error && (
        <p className="field-error" id={errorId}>
          {error}
        </p>
      )}
    </div>
  );
}
